import { Component, OnInit } from '@angular/core';

import { Auth0Service } from '@geneplanet/ngx-auth0';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './analytics.service';
import {
  AnalyticsService as Analytics,
  AnalyticsIdentifyType,
} from '@geneplanet/ngx-utils';
import {
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
} from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Auth0Account } from './accounts/interfaces/accounts.types';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';
import { LayoutService } from './core/services/layout.service';
import { Layout } from './core/interfaces';
import { LanguageService } from './shared/services/language.service';
import { SideMenuService } from './core/services/side-menu.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { CommonService } from '@geneplanet/ngx-shop/src/lib/shared';
import { NavigationEnd, Router } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  GpuMediaQueryBreakpoints,
  GPU_MEDIA_QUERIES,
} from '@geneplanet/ngx-ui/src/lib/layout';
import { SentryErrorService } from './sentry-error.service';
import { IntentService } from './shared/services/intent/intent.service';
import { UserIntentState } from './shared/interfaces/intent.interface';
import { of } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  public get localize(): LocalizeRouterService {
    return this._localize;
  }
  public set localize(value: LocalizeRouterService) {
    this._localize = value;
  }
  layout: Layout;
  environment = environment;
  account?: Auth0Account = null;
  language: string;
  hubspotNewsletter: {
    formId: string;
    portalId: string;
    submitTextKey: string;
  };
  mainMarginTop = 0;
  sideMenuWidth: number;
  isCheckout: boolean;
  isXlUp: boolean;

  private routerUrl: string;
  private isReloading: boolean;

  constructor(
    private readonly auth0: Auth0Service,
    private readonly analytics: AnalyticsService,
    public layoutService: LayoutService,
    private commonService: CommonService,
    private _localize: LocalizeRouterService,
    public languageService: LanguageService,
    private sideMenuService: SideMenuService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private modals: NgbModal,
    private analyticsService: Analytics,
    private sentryErrorService: SentryErrorService,
    private intentService: IntentService
  ) {
    this.sideMenuService.state$.subscribe(({ collapsed, opened }) => {
      if (!opened) {
        this.sideMenuWidth = 0;
      } else {
        this.sideMenuWidth = collapsed
          ? 72
          : this.sideMenuService.sideMenuWidth;
      }
    });

    const location = this.commonService.getLocationFromCode(
      this.localize.parser.currentLang
    );

    this.languageService.onLocationChange(location);

    this.intentService.state$
      .pipe(
        first(),
        switchMap((state: UserIntentState) =>
          state ? this.intentService.verifyIntentActive(state) : of(null)
        )
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.analytics.init();

    if (environment.rudderStackEnabled) {
      this.sentryErrorService.errorOnMissingUserId();
    }

    this.auth0.tokens.current$
      .pipe(
        map((token: string) =>
          new JwtHelperService().decodeToken(JSON.parse(token || '{}').idToken)
        ),
        delay(0),
        filter(() => !this.isReloading)
      )
      .subscribe({
        next: (account: Auth0Account) => {
          this.account = account;

          this.sentryErrorService.setAccount(account);

          // Add user to Sentry
          if (environment.sentry?.logUser) {
            Sentry.setUser({ auth0: account?.sub });
          }
          // If user isn't identified while logged in
          if (environment.rudderStackEnabled && account) {
            this.identifyUser(account);
          }
        },
        error: (error) => {
          Sentry.captureMessage(
            `Failed to verify if user is identified: ${error}`
          );
        },
      });

    this.auth0.tokens.otherPageChange$.subscribe(() => {
      this.isReloading = true;
      window.location.reload();
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => {
          // Remove additional parameter from fragment - HS
          const splits = event.url.split('#');
          if (splits.length > 1) {
            splits[1] = splits[1].split('-')[0];
          }
          return splits.join('#');
        }),
        distinctUntilChanged()
      )
      .subscribe((url: string) => {
        this.routerUrl = url;
        this.modals.dismissAll();
      });

    this.layoutService.layout$.subscribe((layout: Layout) => {
      this.layout = layout;
      this.isCheckout = layout?.footer?.bottomOffset === 'checkout';
    });
    this.layoutService.headerHeightChange$.subscribe(
      (height: number) => (this.mainMarginTop = height)
    );

    this.breakpointObserver
      .observe(GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.XL_UP])
      .subscribe((value: BreakpointState) => (this.isXlUp = value.matches));
  }

  get hasSideMenu() {
    return (
      (this.account && !this.layout?.sideMenu?.hide) ||
      this.layout?.sideMenu?.hasClass
    );
  }

  get hasFooterMenu() {
    return this.account && !this.layout?.footer?.hideMobileNav;
  }

  public showBottomOffset(): boolean {
    return !!this.layout?.footer?.bottomOffset;
  }

  public isProductsPage(): boolean {
    return this.routerUrl?.startsWith('/products/');
  }

  public isCartOrCheckoutPage(): boolean {
    return (
      this.routerUrl?.startsWith('/cart') ||
      this.routerUrl?.startsWith('/checkout/')
    );
  }

  public identifyUser(account: Auth0Account) {
    this.analyticsService
      .loaded()
      .pipe(
        filter((loaded) => loaded),
        first()
      )
      .subscribe(() => {
        this.analyticsService.setUser({
          userId: account.sub,
          traits: {
            type: AnalyticsIdentifyType.HI_NORMAL,
            customerId: account['http://schemas.nomnio.com/2017/5/customer'],
          },
        });
      });
  }
}
